<template>
  <div class="inv">
    <page-header
      title="投资人驾驶舱"
      :crumbs="['驾驶舱', '投资人视角']"
      :subtitle="`出资与回款全貌 · 截至 ${todayText}`"
    >
      <template #actions>
        <span class="tag" :class="scopeTagClass">{{ scopeLabel }}</span>
        <button class="btn" :disabled="loading" @click="load">刷新</button>
        <button class="btn" @click="go('/afterInvest/assetRisk')">资产风险</button>
      </template>
    </page-header>

    <!-- 口径提示 -->
    <p v-if="scopeHint" class="scope-hint" :class="scopeHintClass">{{ scopeHint }}</p>

    <!-- 资金总览 -->
    <section class="fund-grid">
      <div class="fund">
        <div class="fund__label">累计出资</div>
        <div class="fund__value num" :title="fmtMoney(d.totalInvestAmount)">{{ fmtUnit(d.totalInvestAmount) }}</div>
        <div class="fund__sub">在投协议 {{ d.agreementCount }} 个 · 品牌 {{ d.brandCount }} 个 · 门店 {{ d.storeCount }} 家</div>
      </div>

      <div class="fund">
        <div class="fund__label">累计已回收</div>
        <div class="fund__value num" :title="fmtMoney(d.totalRecoveredAmount)">{{ fmtUnit(d.totalRecoveredAmount) }}</div>
        <div class="fund__sub">本月回款 <b class="num">{{ fmtUnit(d.monthReceivedAmount) }}</b> / 本月应收 <b class="num">{{ fmtUnit(d.monthReceivableAmount) }}</b></div>
      </div>

      <div class="fund">
        <div class="fund__label">在投余额</div>
        <div class="fund__value num" :title="fmtMoney(d.outstandingAmount)">{{ fmtUnit(d.outstandingAmount) }}</div>
        <div class="fund__sub">尚未收回的本金，占出资 {{ outstandingPct }}</div>
      </div>

      <div class="fund" :class="{ 'is-alert': hasRisk }">
        <div class="fund__label">欠款 / 风险敞口</div>
        <div class="fund__value num is-danger" :title="fmtMoney(d.arrearsAmount)">{{ fmtUnit(d.arrearsAmount) }}</div>
        <div class="fund__sub">
          <template v-if="hasRisk">
            风险协议 <b class="num">{{ d.riskAgreementCount }}</b> 个 · 敞口 <b class="num">{{ fmtUnit(d.riskExposureAmount) }}</b>
          </template>
          <template v-else>暂无逾期欠款与风险协议</template>
        </div>
      </div>
    </section>

    <!-- 回款进度 -->
    <section class="card progress-card">
      <div class="card__head">
        <span class="card__title">回款进度</span>
        <span class="card__meta">已回收占累计出资比例</span>
      </div>
      <div class="progress-card__body">
        <div class="progress__rate num">{{ recoveryPct }}</div>
        <div class="progress__bar">
          <span class="progress__fill" :style="{ width: recoveryPct }"></span>
        </div>
        <div class="progress__legend">
          <span><i class="dot dot--done"></i>已回收 <b class="num">{{ fmtUnit(d.totalRecoveredAmount) }}</b></span>
          <span><i class="dot dot--left"></i>在投余额 <b class="num">{{ fmtUnit(d.outstandingAmount) }}</b></span>
          <span><i class="dot dot--risk"></i>其中风险敞口 <b class="num">{{ fmtUnit(d.riskExposureAmount) }}</b></span>
        </div>
      </div>
    </section>

    <!-- 趋势 + 排名 -->
    <section class="inv-main">
      <div class="card">
        <div class="card__head">
          <span class="card__title">近 12 个月回款趋势</span>
          <span class="card__meta">按实际到账月份统计</span>
        </div>
        <div ref="trendRef" class="trend-canvas"></div>
      </div>

      <div class="card">
        <div class="card__head">
          <span class="card__title">规模与风险</span>
        </div>
        <div class="stat-list">
          <div class="stat-row">
            <span class="stat-row__label">在投协议</span>
            <span class="stat-row__value num">{{ d.agreementCount }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-row__label">在投门店</span>
            <span class="stat-row__value num">{{ d.storeCount }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-row__label">在投品牌</span>
            <span class="stat-row__value num">{{ d.brandCount }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-row__label">风险协议</span>
            <span class="stat-row__value num" :class="{ 'is-danger': d.riskAgreementCount > 0 }">{{ d.riskAgreementCount }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-row__label">累计应收</span>
            <span class="stat-row__value num">{{ fmtUnit(d.totalReceivableAmount) }}</span>
          </div>
        </div>
        <button class="link link--block" @click="go('/afterInvest/todo')">查看高风险待办 →</button>
      </div>
    </section>

    <!-- 品牌维度 -->
    <section class="card">
      <div class="card__head">
        <span class="card__title">品牌维度</span>
        <span class="card__meta">按在投余额降序</span>
      </div>
      <DataTable
        :columns="brandColumns"
        :data="d.brandRanking"
        :total="0"
        row-key="code"
        empty-text="暂无出资数据"
      >
        <template #cell-recoveryRate="{ row }">
          <span class="mini-bar">
            <span class="mini-bar__fill" :style="{ width: pct(row.recoveryRate) }"></span>
          </span>
          <span class="mini-bar__text num">{{ pct(row.recoveryRate) }}</span>
        </template>
      </DataTable>
    </section>

    <!-- 门店维度 -->
    <section class="card" style="margin-top: 16px;">
      <div class="card__head">
        <span class="card__title">门店维度</span>
        <span class="card__meta">仅含门店级协议（品牌级协议不做出资分摊）</span>
      </div>
      <DataTable
        :columns="storeColumns"
        :data="d.storeRanking"
        :total="0"
        row-key="code"
        empty-text="暂无门店级出资数据"
      >
        <template #cell-recoveryRate="{ row }">
          <span class="mini-bar">
            <span class="mini-bar__fill" :style="{ width: pct(row.recoveryRate) }"></span>
          </span>
          <span class="mini-bar__text num">{{ pct(row.recoveryRate) }}</span>
        </template>
      </DataTable>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import * as echarts from 'echarts'
import PageHeader from '@/components/PageHeader.vue'
import DataTable from '@/components/DataTable.vue'
import dashboardService from '@/services/dashboardService'

const router = useRouter()
const loading = ref(false)
const trendRef = ref(null)
let chart = null

const d = reactive({
  totalInvestAmount: 0,
  totalRecoveredAmount: 0,
  outstandingAmount: 0,
  recoveryRate: 0,
  totalReceivableAmount: 0,
  arrearsAmount: 0,
  riskExposureAmount: 0,
  monthReceivedAmount: 0,
  monthReceivableAmount: 0,
  agreementCount: 0,
  storeCount: 0,
  brandCount: 0,
  riskAgreementCount: 0,
  monthlyTrend: [],
  brandRanking: [],
  storeRanking: [],
  scope: 'tenant'
})

const todayText = computed(() => {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
})

const toNumber = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/* 大额用「万元 / 亿元」口径，便于快速读数；完整值放在 title 上 */
const fmtUnit = (v) => {
  const n = toNumber(v)
  const abs = Math.abs(n)
  if (abs >= 1e8) return `${(n / 1e8).toFixed(2)} 亿元`
  if (abs >= 1e4) return `${(n / 1e4).toFixed(1)} 万元`
  return `${n.toFixed(0)} 元`
}

const fmtMoney = (v) => `¥${toNumber(v).toLocaleString('zh-CN', { maximumFractionDigits: 0 })}`

const pct = (v) => `${(toNumber(v) * 100).toFixed(1)}%`

/* ---------- 当前视角：决定了「在看谁的账」 ---------- */
// platform = 平台方机构（编号 1）看全部出资方；tenant = 本方；all = 无租户上下文兜底
const isPlatform = computed(() => d.scope === 'platform')

/* 金额列复用 DataTable 的 money 类型：自动右对齐 + 等宽数字 */
const moneyCol = (prop, label, minWidth) => ({
  prop,
  label,
  minWidth,
  type: 'money',
  formatter: (row) => fmtMoney(row[prop])
})

// 平台方看的是全部出资方汇总，列头不再强调「本方」
const investLabel = computed(() => (isPlatform.value ? '出资额' : '本方出资'))

const brandColumns = computed(() => [
  { prop: 'name', label: '品牌', minWidth: 160 },
  moneyCol('investAmount', investLabel.value, 140),
  moneyCol('recoveredAmount', '已回收', 140),
  moneyCol('outstandingAmount', '在投余额', 140),
  { prop: 'recoveryRate', label: '回本率', minWidth: 150 },
  { prop: 'storeCount', label: '门店数', width: 90, align: 'right', type: 'number' }
])

const storeColumns = computed(() => [
  { prop: 'name', label: '门店', minWidth: 200 },
  moneyCol('investAmount', investLabel.value, 140),
  moneyCol('recoveredAmount', '已回收', 140),
  moneyCol('outstandingAmount', '在投余额', 140),
  { prop: 'recoveryRate', label: '回本率', minWidth: 150 }
])

const recoveryPct = computed(() => pct(d.recoveryRate))
const outstandingPct = computed(() => {
  const invest = toNumber(d.totalInvestAmount)
  if (invest <= 0) return '—'
  return pct(toNumber(d.outstandingAmount) / invest)
})
const hasRisk = computed(() => toNumber(d.arrearsAmount) > 0 || toNumber(d.riskAgreementCount) > 0)

const scopeLabel = computed(() => {
  if (d.scope === 'platform') return '平台方视角 · 全部出资方'
  if (d.scope === 'all') return '未识别出资方 · 全量'
  return '本方视角'
})

const scopeTagClass = computed(() => {
  if (d.scope === 'platform') return 'tag-info'
  if (d.scope === 'all') return 'tag-warning'
  return 'tag-neutral'
})

const scopeHint = computed(() => {
  if (d.scope === 'platform') {
    return '当前机构为平台方，展示全部出资方的汇总数据（不按单一出资方折算）。'
  }
  if (d.scope === 'all') {
    return '当前未识别到出资方身份（无租户上下文），以下为全量口径数据。'
  }
  return ''
})

const scopeHintClass = computed(() => (isPlatform.value ? 'is-info' : 'is-warn'))

const renderTrend = async () => {
  await nextTick()
  if (!trendRef.value) return
  if (!chart) chart = echarts.init(trendRef.value)
  const rows = d.monthlyTrend || []
  chart.setOption({
    grid: { left: 8, right: 12, top: 16, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const p = params?.[0]
        if (!p) return ''
        return `${p.axisValue}<br/>回款 ${fmtMoney(p.data)}`
      }
    },
    xAxis: {
      type: 'category',
      data: rows.map(r => r.month),
      axisLine: { lineStyle: { color: '#DCE0E8' } },
      axisLabel: { color: '#8A90A2', fontSize: 11 }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#8A90A2',
        fontSize: 11,
        formatter: (v) => (Math.abs(v) >= 1e4 ? `${(v / 1e4).toFixed(0)}万` : v)
      },
      splitLine: { lineStyle: { color: '#F5F6F8' } }
    },
    series: [
      {
        name: '回款',
        type: 'bar',
        barMaxWidth: 26,
        itemStyle: { color: '#2C5BE8', borderRadius: [4, 4, 0, 0] },
        data: rows.map(r => toNumber(r.receivedAmount))
      }
    ]
  })
}

const onResize = () => chart && chart.resize()

const load = async () => {
  loading.value = true
  try {
    const data = await dashboardService.getInvestorStats()
    const src = data || {}
    Object.assign(d, {
      totalInvestAmount: src.totalInvestAmount ?? 0,
      totalRecoveredAmount: src.totalRecoveredAmount ?? 0,
      outstandingAmount: src.outstandingAmount ?? 0,
      recoveryRate: src.recoveryRate ?? 0,
      totalReceivableAmount: src.totalReceivableAmount ?? 0,
      arrearsAmount: src.arrearsAmount ?? 0,
      riskExposureAmount: src.riskExposureAmount ?? 0,
      monthReceivedAmount: src.monthReceivedAmount ?? 0,
      monthReceivableAmount: src.monthReceivableAmount ?? 0,
      agreementCount: src.agreementCount ?? 0,
      storeCount: src.storeCount ?? 0,
      brandCount: src.brandCount ?? 0,
      riskAgreementCount: src.riskAgreementCount ?? 0,
      monthlyTrend: src.monthlyTrend || [],
      brandRanking: src.brandRanking || [],
      storeRanking: src.storeRanking || [],
      scope: src.scope || 'tenant'
    })
    renderTrend()
  } catch (e) {
    console.error('获取投资人驾驶舱数据失败', e)
  } finally {
    loading.value = false
  }
}

const go = (path) => router.push(path)

onMounted(() => {
  window.addEventListener('resize', onResize)
  load()
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
  if (chart) {
    chart.dispose()
    chart = null
  }
})
</script>

<style scoped>
.inv {
  width: 100%;
}

.scope-hint {
  margin: 0 0 12px;
  padding: 8px 12px;
  font-size: 12px;
  border-radius: var(--radius-control);
}

/* 平台方视角是「正常状态」，用信息色；口径不明才是需要警示的 */
.scope-hint.is-info {
  color: var(--info-text);
  background: var(--info-bg);
}

.scope-hint.is-warn {
  color: var(--warning-text);
  background: var(--warning-bg);
}

/* ---------- 资金总览 ---------- */
.fund-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--space-4);
}

.fund {
  padding: var(--space-5);
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-sm);
}

.fund.is-alert {
  border-color: var(--danger-fill);
  background: linear-gradient(180deg, #FFF 0%, var(--danger-bg) 100%);
}

.fund__label {
  font-size: 13px;
  color: var(--text-secondary);
}

.fund__value {
  margin-top: 10px;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.15;
  color: var(--text-primary);
}

.fund__value.is-danger {
  color: var(--danger-text);
}

.fund__sub {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--border);
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ---------- 回款进度 ---------- */
.progress-card {
  margin-top: var(--space-4);
}

.progress-card__body {
  padding: var(--space-5);
}

.progress__rate {
  font-size: 24px;
  font-weight: 600;
  color: var(--brand-500);
}

.progress__bar {
  margin-top: 10px;
  height: 10px;
  background: var(--neutral-50);
  border-radius: 999px;
  overflow: hidden;
}

.progress__fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--brand-500), #6E90F0);
  border-radius: 999px;
  transition: width .3s ease;
}

.progress__legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6);
  margin-top: 12px;
  font-size: 12px;
  color: var(--text-secondary);
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.dot--done { background: var(--brand-500); }
.dot--left { background: var(--neutral-300); }
.dot--risk { background: var(--danger-fill); }

/* ---------- 卡片 ---------- */
.card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-sm);
}

.card__head {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 14px var(--space-5);
  border-bottom: 1px solid var(--border);
}

.card__title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.card__meta {
  font-size: 12px;
  color: var(--text-tertiary);
}

.link {
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  font-size: 12px;
  color: var(--info-text);
  cursor: pointer;
}

.link:hover {
  color: var(--brand-600);
}

.link--block {
  display: block;
  margin: 12px var(--space-5) 14px;
  text-align: left;
}

/* ---------- 趋势 + 规模 ---------- */
.inv-main {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 1fr);
  gap: var(--space-4);
  margin-top: var(--space-4);
  margin-bottom: var(--space-4);
}

.trend-canvas {
  height: 300px;
  padding: 8px 0;
}

.stat-list {
  padding: 6px var(--space-5);
}

.stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 0;
  border-bottom: 1px solid var(--border);
  font-size: 13px;
}

.stat-row:last-child {
  border-bottom: none;
}

.stat-row__label {
  color: var(--text-secondary);
}

.stat-row__value {
  font-weight: 600;
  color: var(--text-primary);
}

.stat-row__value.is-danger {
  color: var(--danger-text);
}

/* ---------- 表格内迷你进度 ---------- */
.mini-bar {
  display: inline-block;
  width: 72px;
  height: 6px;
  vertical-align: middle;
  background: var(--neutral-50);
  border-radius: 999px;
  overflow: hidden;
}

.mini-bar__fill {
  display: block;
  height: 100%;
  background: var(--brand-500);
  border-radius: 999px;
}

.mini-bar__text {
  margin-left: 8px;
  font-size: 12px;
  color: var(--text-secondary);
}

/* ---------- 响应式 ---------- */
@media (max-width: 1440px) {
  .fund-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .inv-main {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
